/**
 * Outcomes-dashboard preferences: the preference scope key, the persisted
 * shape, and the selectors consumers read prefs through.
 *
 * Metric identity and default visibility come from the canonical metric
 * registry (`@transfr-inc/dashboard-components/outcome-metrics`); this module
 * never defines its own metric list. Selectors here are pure functions of a prefs object (plus, where
 * noted, the set of scopes the current user can access). Keeping
 * scope-resolution in selectors lets the hook layer stay thin and lets the
 * security-sensitive pruning be unit-tested in isolation.
 */

import { defaultVisibleIds } from "@transfr-inc/dashboard-components/outcome-metrics";

/** Preference scope identifier passed to `userPreferencesService`. */
export const OUTCOMES_PREFS_KEY = "outcomes";

/**
 * The current academic year as a concrete `{ startDate, endDate }` range. The
 * academic year starts August 1, so a date in January–July belongs to the prior
 * calendar year's academic year, which runs to the following July 31. Mirrors
 * the core-platform `DateRangePreset.ACADEMIC_YEAR` resolution so the client's
 * default range matches what the server derives and persists.
 *
 * @param {Date} [today] - Reference date; defaults to now.
 * @returns {{ startDate: Date, endDate: Date }}
 */
export function academicYearRange(today = new Date()) {
  const startYear =
    today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    startDate: new Date(startYear, 7, 1),
    endDate: new Date(startYear + 1, 6, 31),
  };
}

/**
 * Metric sort modes. Values match the core-platform `OutcomeSort` enum exactly.
 */
export const OutcomeSort = Object.freeze({
  METRIC_ASC: "metric_asc",
  METRIC_DESC: "metric_desc",
  NAME_ASC: "name_asc",
  NAME_DESC: "name_desc",
});

/**
 * Wire `sort` tokens per {@link OutcomeSort} value: comma-separated `field:dir`
 * pairs the breakdown endpoints apply in order (`field`: `value`|`name`).
 *
 * The metric sorts carry a `name:asc` secondary token because the endpoints
 * return a row for every entity in scope — many tie at 0, and only the name
 * breaks those ties into a stable, page-safe order.
 */
const SORT_TOKENS = Object.freeze({
  [OutcomeSort.METRIC_DESC]: "value:desc,name:asc",
  [OutcomeSort.METRIC_ASC]: "value:asc,name:asc",
  [OutcomeSort.NAME_ASC]: "name:asc,value:desc",
  [OutcomeSort.NAME_DESC]: "name:desc,value:desc",
});

/**
 * The breakdown endpoints' `sort` param for a sort preference. An unknown or
 * missing value reads as the `metric_desc` default, so a stored value from a
 * retired mode can never send an unsortable request.
 *
 * The preference is unvalidated stored data, so membership is checked with
 * `Object.hasOwn`: a plain lookup would resolve inherited `Object.prototype`
 * keys (`constructor`, `toString`) to non-token values and send them on the wire.
 *
 * @param {string} [sort] - One of {@link OutcomeSort}.
 * @returns {string} Wire `sort` value.
 */
export function sortTokens(sort) {
  return sort !== undefined && Object.hasOwn(SORT_TOKENS, sort)
    ? SORT_TOKENS[sort]
    : SORT_TOKENS[OutcomeSort.METRIC_DESC];
}

/**
 * Persisted outcomes-dashboard preferences.
 *
 * An absent (`undefined`) collection means "all" (e.g. no org restriction);
 * the server rejects an empty `selectedMetricIds` and an all-empty
 * `selectedClassroomIdsByOrg`, so omit a field rather than sending an empty
 * value to express "all".
 *
 * @typedef {object} OutcomesPrefs
 * @property {string[]} [selectedMetricIds] - Metric ids the user has chosen to
 *   show (1–50). Absent falls back to the registry defaults.
 * @property {string[]} [metricOrder] - Global display order over the full
 *   metric catalog, independent of selection (≤50).
 * @property {string[]} [selectedOrgCodes] - Org codes the user has scoped to
 *   (each 1–5 chars, 1–500 entries). Pruned against accessible orgs on read;
 *   absent means all orgs.
 * @property {Object.<string, string[]>} [selectedClassroomIdsByOrg] - Classroom
 *   ids keyed by org code (`{ orgCode: [classroomId, ...] }`, ≤2000 per org).
 *   Pruned against accessible classrooms on read; absent means all classrooms.
 * @property {string[]} [selectedPrograms] - Program ids filter (each ≤64 chars,
 *   ≤50 entries).
 * @property {string[]} [selectedModules] - Module ids filter for module-aware
 *   metrics (each ≤64 chars, ≤50 entries); absent means all modules.
 * @property {string} [sort] - One of {@link OutcomeSort}. Defaults to
 *   `metric_desc`.
 * @property {object} [dateFilter] - Date-range filter. A preset filter may carry
 *   just `preset` (the server resolves `start`/`end` on each load); a custom
 *   filter omits `preset` and requires both `start` and `end`.
 * @property {string} [dateFilter.preset] - One of the library's
 *   `DateRangePreset` values
 *   (`@transfr-inc/dashboard-components/outcome-metrics`), or absent for a
 *   custom range.
 * @property {string} [dateFilter.start] - Inclusive range start (`YYYY-MM-DD`).
 * @property {string} [dateFilter.end] - Inclusive range end (`YYYY-MM-DD`).
 * @property {Object.<string, object>} [metricState] - Opaque per-metric UI
 *   state keyed by metric id (≤50 keys).
 * @property {boolean} [includeAnonymousLearners] - Keeps anonymous learners in
 *   learner lists and the Per Learner export. Defaults to `false`, hiding them.
 * @property {boolean} [includeEmptyRows] - Keeps organizations, classrooms and
 *   learners with no data for the metric in the breakdown. Defaults to `false`,
 *   hiding them.
 */

/**
 * Registry-driven default preferences for a user with nothing stored. Used for
 * first paint before the server responds and for impersonated sessions (which
 * never read or write stored prefs).
 *
 * @param {readonly import("@transfr-inc/dashboard-components/outcome-metrics").MetricDefinition[]} registry
 *
 * "All"-meaning collections (orgs, classrooms, programs, modules) and
 * `dateFilter` are omitted rather than set empty: the server treats their
 * absence as "all" and rejects empty values, and it backfills `dateFilter` to
 * the academic-year preset, so this object round-trips without a validation
 * error.
 *
 * @returns {OutcomesPrefs}
 */
export function defaultsForOutcomes(registry) {
  return {
    selectedMetricIds: defaultVisibleIds(registry),
    sort: OutcomeSort.METRIC_DESC,
  };
}

/**
 * Resolves the metric ids to render. Drops ids no longer in the registry so a
 * retired (or renamed) metric can't linger in a stored selection, and falls
 * back to the registry defaults whenever that leaves nothing to show — whether
 * the user made no explicit selection or every stored id has since left the
 * registry. Falling back rather than returning `[]` keeps a stale selection
 * from rendering an empty tile row.
 *
 * @param {readonly import("@transfr-inc/dashboard-components/outcome-metrics").MetricDefinition[]} registry
 * @param {OutcomesPrefs} [prefs]
 * @returns {string[]}
 */
export function visibleMetrics(registry, prefs) {
  const known = new Set(registry.map((m) => m.id));
  const selected = prefs?.selectedMetricIds;
  const visible = Array.isArray(selected)
    ? selected.filter((id) => known.has(id))
    : [];
  if (visible.length === 0) {
    return defaultsForOutcomes(registry).selectedMetricIds;
  }
  return visible;
}

/**
 * Orders a user's visible metric ids by their `metricOrder` preference.
 * `metricOrder` is a global ranking over the whole catalog (independent of
 * selection), so visible ids found in it sort by their index there; any not
 * listed keep their incoming (selection) order and follow. A reorder in the
 * picker rewrites `metricOrder`, which propagates here with no other change.
 *
 * Ids are deduped defensively: a duplicated stored id must not produce two
 * tiles, duplicate React keys, or duplicate DOM ids downstream. The first
 * occurrence wins, preserving selection order.
 *
 * @param {string[]} ids - Visible ids, in selection order.
 * @param {string[]} [metricOrder] - Global display order over the catalog.
 * @returns {string[]}
 */
export function orderVisibleIds(ids, metricOrder) {
  const uniqueIds = [...new Set(ids)];
  if (!Array.isArray(metricOrder) || metricOrder.length === 0) {
    return uniqueIds;
  }

  const rank = new Map(metricOrder.map((id, index) => [id, index]));
  const unranked = metricOrder.length;

  return [...uniqueIds].sort((a, b) => {
    const rankA = rank.get(a) ?? unranked;
    const rankB = rank.get(b) ?? unranked;
    return rankA - rankB;
  });
}

/**
 * Prunes a stored id selection against the set the user can access today,
 * dropping out-of-scope ids silently. This is the IDOR-scope guard: a
 * selection persisted under broader access (or tampered with) can never widen
 * the request beyond what the user may currently see.
 *
 * `needsReselect` is true when a non-empty stored selection pruned down to
 * empty — the consumer should prompt the user to re-select rather than treat
 * `[]` as "select none" (which would silently mean "everything accessible").
 *
 * @param {Array<string|number>} [selected] - Stored selection
 * @param {Set<string|number>|Iterable<string|number>} [accessible] - Ids accessible now
 * @returns {{ ids: Array<string|number>, needsReselect: boolean }}
 */
export function pruneToAccessible(selected, accessible) {
  const allowed =
    accessible instanceof Set ? accessible : new Set(accessible ?? []);
  const stored = Array.isArray(selected) ? selected : [];
  const ids = stored.filter((id) => allowed.has(id));
  return { ids, needsReselect: stored.length > 0 && ids.length === 0 };
}

/**
 * Effective org-code scope: the stored `selectedOrgCodes` pruned to the orgs
 * the user can access now. See {@link pruneToAccessible} for the contract.
 *
 * @param {OutcomesPrefs} [prefs]
 * @param {Set<string>|Iterable<string>} [accessibleOrgCodes]
 * @returns {{ ids: string[], needsReselect: boolean }}
 */
export function effectiveOrgs(prefs, accessibleOrgCodes) {
  return pruneToAccessible(prefs?.selectedOrgCodes, accessibleOrgCodes);
}

/**
 * Effective classroom scope: the per-org `selectedClassroomIdsByOrg` map
 * flattened to a single id list and pruned to the classrooms the user can
 * access now. See {@link pruneToAccessible}.
 *
 * @param {OutcomesPrefs} [prefs]
 * @param {Set<string>|Iterable<string>} [accessibleClassroomIds]
 * @returns {{ ids: string[], needsReselect: boolean }}
 */
export function effectiveClassrooms(prefs, accessibleClassroomIds) {
  const byOrg = prefs?.selectedClassroomIdsByOrg;
  const flat =
    byOrg && typeof byOrg === "object" ? Object.values(byOrg).flat() : [];
  return pruneToAccessible(flat, accessibleClassroomIds);
}
